import {
  CloseCircleOutlined,
  ColumnWidthOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { DateSkipper, GraviButton, Horizontal, Texto } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { Select, Switch, Tooltip, TreeSelect } from 'antd'
import moment from 'moment'
import { useMemo, useState } from 'react'
import { useDirectiveContext } from './DirectiveContext'
const { Option } = Select
const { SHOW_PARENT } = TreeSelect
export function DefaultDirectiveFilters({ SwitchComponent }) {
  const { filters, setFilters, serverParams, setSelectedDate } = useDirectiveContext()
  const [pickerDate, setPickerDate] = useState(moment().local().toDate())
  const onDayChange = (date) => {
    setPickerDate(date)
    setSelectedDate(date)
  }

  return (
    <>
      <DeltaSelect />
      <DirectiveFilterSelect
        tooltip='Filter By Supplier'
        placeholder={
          <>
            <UserOutlined className='filter-icon' />
            Suppliers
          </>
        }
        onChange={(value) => setFilters((filters) => ({ ...filters, suppliers: value }))}
        value={filters?.suppliers}
        items={serverParams?.suppliers}
      />
      <DirectiveFilterTreeSelect
        tooltip='Filter By Supply Zone'
        placeholder={
          <>
            <EnvironmentOutlined className='filter-icon' />
            Supply Zones
          </>
        }
        selectedValue={filters?.terminals}
        treeDataObject={serverParams?.supplyZones}
        onChange={(value) => setFilters((filters) => ({ ...filters, terminals: value }))}
      />
      <DirectiveFilterSelect
        tooltip='Filter By Product'
        placeholder={
          <>
            <ExperimentOutlined className='filter-icon' />
            Products
          </>
        }
        onChange={(value) => setFilters((filters) => ({ ...filters, products: value }))}
        value={filters?.products}
        items={serverParams?.products}
      />
      <Tooltip title='Clear Filters'>
        <GraviButton
          onClick={() => setFilters({})}
          className='mr-3'
          style={{ maxHeight: '25px', padding: '0 5px' }}
          icon={<CloseCircleOutlined />}
        />
      </Tooltip>
      {SwitchComponent}
      <Horizontal verticalCenter style={{ overflow: 'visible' }}>
        <DateSkipper daysToSkip={1} onChange={onDayChange} format={formatters.DATE} startDate={pickerDate} />
      </Horizontal>
    </>
  )
}
export function PlannedSwitch() {
  const { setViewingPlanned, viewingPlanned } = useDirectiveContext()
  return (
    <Horizontal verticalCenter style={{ overflow: 'visible' }}>
      <Texto style={{ whiteSpace: 'nowrap' }} weight='bold' textTransform='uppercase' className='mr-3'>
        Include Planned
      </Texto>
      <Switch value={viewingPlanned} onChange={() => setViewingPlanned(!viewingPlanned)} />
    </Horizontal>
  )
}
export function WeeklySwitch() {
  const { setViewingWeekly, viewingWeekly } = useDirectiveContext()
  return (
    <Horizontal style={{ overflow: 'visible' }} verticalCenter className='mx-3'>
      <Texto weight='bold' textTransform='uppercase' style={{ whiteSpace: 'nowrap' }} className='mr-3'>
        Include Weekly
      </Texto>
      <Switch value={viewingWeekly} onChange={() => setViewingWeekly(!viewingWeekly)} />
    </Horizontal>
  )
}

function DirectiveFilterSelect({ tooltip, placeholder, onChange, value, items = [] }) {
  return (
    <Tooltip placement='topLeft' mouseEnterDelay={2} title={tooltip}>
      <Select
        mode='multiple'
        allowClear
        showSearch
        style={{ width: '100%', maxWidth: '100%' }}
        placeholder={placeholder}
        onChange={onChange}
        value={value}
        className='mr-2'
        maxTagCount='responsive'
        filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
      >
        {items.map((item) => (
          <Option key={item.id}>{item.name}</Option>
        ))}
      </Select>
    </Tooltip>
  )
}
function DirectiveFilterTreeSelect({ tooltip, placeholder, onChange, selectedValue, treeDataObject = {} }) {
  const treeData = useMemo(() => {
    if (!treeDataObject) return []
    const supplyZonesArray = Object.entries(treeDataObject)
    return supplyZonesArray.map(([parent, children]) => {
      return {
        title: parent,
        value: parent,
        key: parent,
        children: children.map((child) => ({ ...child, key: child.title, value: child.title })),
      }
    })
  }, [treeDataObject])

  const parentItems = Object.keys(treeDataObject)
  const isParent = (value) => parentItems.includes(value)
  const getChildren = (value) => treeDataObject?.[value]?.map((child) => child.title)

  const handleChange = (values) => {
    const listOfTerminals = values.map((value) => {
      if (isParent(value)) {
        return getChildren(value)
      }
      return value
    })
    onChange(listOfTerminals.flat())
  }

  return (
    <Tooltip placement='topLeft' mouseEnterDelay={2} title={tooltip}>
      <TreeSelect
        treeCheckable
        showCheckedStrategy={SHOW_PARENT}
        treeData={treeData || []}
        allowClear
        showSearch
        style={{ width: '100%', maxWidth: '100%' }}
        placeholder={placeholder}
        onChange={handleChange}
        value={selectedValue}
        className='mr-2'
        maxTagCount='responsive'
        multiple
        treeDefaultExpandAll
        showArrow={false}
      />
    </Tooltip>
  )
}
function DeltaSelect() {
  const { setFilters, filters } = useDirectiveContext()
  return (
    <Select
      placeholder={
        <>
          <ColumnWidthOutlined className='pr-3' />
          Status
        </>
      }
      onChange={(value) => setFilters((filters) => ({ ...filters, status: value }))}
      style={{ minWidth: 150 }}
      allowClear
      className='mr-3'
      value={filters?.status}
      onClear={() => setFilters((filters) => ({ ...filters, status: undefined }))}
    >
      <Option value='outside'>Off Target</Option>
      <Option value='over'>Over</Option>
      <Option value='under'>Under</Option>
    </Select>
  )
}
